import React from "react";
import Image from "next/image";
import cx from "clsx";
import dayjs from "@/utils/dayjs";
import { MOMENT_FORMAT } from "@/constants";
import { subscription } from "@/services/subscription";
import { DailyWeatherForecastData } from "@/types/forecast";
import { ACTIVE_DAY } from "@/app/(pages)/Home/constants/shared";
import { getDetailedIconName } from "@/utils/getWeatherIcon";
import { getCityClockFromDay } from "@/utils/cityTime";
import { isNightForDay } from "@/utils/dayNight";
import {
  codeToGlyph,
  getDayDisplayCode,
} from "@/app/(pages)/Home/components/shared/Highcharts/components/WeatherTimeline";

type ForecastDayRowProps = {
  day: DailyWeatherForecastData;
  selectedDay: string;
  t: any;
  i18n: any;
};

const badWeather = ["fog", "snow", "thunder", "rain"];

const ForecastDayRow = ({ day, selectedDay, t, i18n }: ForecastDayRowProps) => {
  const handleClick = () => {
    subscription({
      key: ACTIVE_DAY,
      value: day.date,
    });
  };

  const { todayKey } = getCityClockFromDay(day);
  const isNight = isNightForDay(day);
  const displayCode = getDayDisplayCode(day) ?? day.weatherCode;
  const { code, sun } = getDetailedIconName(displayCode, isNight);

  const dayWeek = dayjs(day.date).locale(i18n.language).format("dd");
  const dateTime = dayjs(day.date, MOMENT_FORMAT).toISOString();
  const formattedDayWeek =
    day.date === todayKey
      ? t("week_forecast.today")
      : dayWeek.slice(0, 1).toUpperCase() + dayWeek.slice(1);
  const formattedDate = dayjs(day.date, MOMENT_FORMAT)
    .locale(i18n.language)
    .format("DD.MM");

  const precipitation = Math.floor(day.precipitation ?? 0);
  const isSelected = selectedDay === day.date;

  const sunClassName = badWeather.includes(codeToGlyph(+code, isNight))
    ? "-top-1.5 scale-105"
    : "top-0";

  return (
    <li className="group relative flex-1">
      <button
        type="button"
        onClick={handleClick}
        className="relative z-10 grid w-full cursor-pointer grid-cols-[5.5rem_4rem_1fr] items-center gap-2 rounded-2xl px-3 py-1.5 text-left"
      >
        <span className="min-w-0">
          <time
            dateTime={dateTime}
            className="block truncate text-base leading-5"
          >
            {formattedDayWeek}
          </time>
          <span className="block text-xs leading-4 text-white/50">
            {formattedDate}
          </span>
        </span>

        <span className="flex items-center gap-1.5">
          <span className="relative h-9 w-9 shrink-0">
            <Image
              src={`/weather/${code}.png`}
              alt={t(`weather.${displayCode}.description`)}
              width={36}
              height={36}
              className="h-9 w-9 object-contain"
            />
            {sun && (
              <Image
                src={`/weather/${isNight ? "0.night" : "0"}.png`}
                alt=""
                aria-hidden
                width={16}
                height={16}
                loading="lazy"
                className={cx(
                  "absolute right-0 z-0 h-auto w-4 -rotate-15",
                  sunClassName,
                )}
              />
            )}
          </span>
          {precipitation > 0 && (
            <span className="text-deep-blue text-xs font-semibold">
              {precipitation}%
            </span>
          )}
        </span>

        <span className="flex items-baseline justify-end gap-1.5 pr-1">
          <span className="text-xl leading-6 font-semibold">
            {Math.floor(day.maxTemperature)}°
          </span>
          <span className="text-sm text-white/50">
            /{Math.floor(day.minTemperature)}°
          </span>
        </span>
      </button>
      {isSelected && (
        <div
          aria-hidden
          className="bg-vivid-blue/40 pointer-events-none absolute top-1/2 left-1/4 -z-1 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl brightness-125"
        />
      )}
      <div
        className={cx(
          "absolute inset-0 rounded-2xl border border-transparent duration-200",
          isSelected ? "v-row-active" : "group-hover:bg-white/5",
        )}
      />
    </li>
  );
};

export default ForecastDayRow;
